import { Match3 } from './index';
import { Immutable } from '@play-co/replicant';

type BoardState = Immutable<Match3.BoardState>;

const getBlockType = (
  board: BoardState,
  { colIndex, rowIndex }: Match3.BoardPosition | undefined,
) => board[colIndex]?.[rowIndex];

export const match3Getters = (Match3: Pick<Match3, 'ruleset'>) => {
  const getMatches = (board: BoardState): Match3.BoardPosition[] => {
    // Convert matches to strings for easier equality checking
    const matches: string[] = [];
    for (let colIndex = 0; colIndex < board.length; ++colIndex) {
      const col = board[colIndex];
      for (let rowIndex = 0; rowIndex < col.length; ++rowIndex) {
        matches.push(...checkSurroundingMatches(board, { colIndex, rowIndex }));
      }
    }

    return Array.from(new Set(matches)).map(Match3.ruleset.stringToPosition);
  };

  const checkSurroundingMatches = (
    board: BoardState,
    position: Match3.BoardPosition,
  ): string[] => {
    const { positionToString, isPositionOnBoard, minMatches } = Match3.ruleset;
    const shape = getBlockType(board, position);
    const rowMatches = [positionToString(position)];
    const colMatches = [...rowMatches];

    // check right
    let neighbour = {
      ...position,
      colIndex: position.colIndex - 1,
    };
    if (
      isPositionOnBoard(neighbour) &&
      getBlockType(board, neighbour) === shape
    ) {
      rowMatches.push(positionToString(neighbour));
    }

    //check left
    neighbour = {
      ...position,
      colIndex: position.colIndex + 1,
    };
    if (
      isPositionOnBoard(neighbour) &&
      getBlockType(board, neighbour) === shape
    ) {
      rowMatches.push(positionToString(neighbour));
    }

    // check bottom
    neighbour = {
      ...position,
      rowIndex: position.rowIndex - 1,
    };
    if (
      isPositionOnBoard(neighbour) &&
      getBlockType(board, neighbour) === shape
    ) {
      colMatches.push(positionToString(neighbour));
    }

    //check top
    neighbour = {
      ...position,
      rowIndex: position.rowIndex + 1,
    };
    if (
      isPositionOnBoard(neighbour) &&
      getBlockType(board, neighbour) === shape
    ) {
      colMatches.push(positionToString(neighbour));
    }

    return [
      ...(rowMatches.length >= minMatches ? rowMatches : []),
      ...(colMatches.length >= minMatches ? colMatches : []),
    ];
  };

  return ({ match3 }: Match3.State) => ({
    getBoardState: () => {
      const { rows, columns } = Match3.ruleset;
      return match3.board.slice(0, columns).map((col) => col.slice(0, rows));
    },

    getBlockType: (position: Match3.BoardPosition) =>
      getBlockType(match3.board, position),

    getTurnCount: () => match3.turns.length,

    getComboMultiplier: () => [...match3.turns].pop()?.matches.length,

    getCurrentMatch: () =>
      [...match3.turns].pop()?.matches[match3.currentMatchIndex],

    getMatches: (): Match3.BoardPosition[] => getMatches(match3.board),

    getMatchSets() {
      const matches = getMatches(match3.board);

      // Create a copy of the board with only matched blocks
      const matchedPositions = matches.reduce((board, position) => {
        const { colIndex, rowIndex } = position;
        if (!board[colIndex]) {
          board[colIndex] = [];
        }
        board[colIndex][rowIndex] = getBlockType(match3.board, position);
        return board;
      }, [] as Match3.BlockType[][]);

      // Search the board for sets of connected blocks
      return matches.reduce((matchSets, position) => {
        const { colIndex, rowIndex } = position;
        const type = matchedPositions[colIndex][rowIndex];
        const matchSet = Match3.ruleset.floodFind(
          matchedPositions,
          position,
          type,
        );
        if (matchSet.length > 0) {
          matchSets.push(matchSet);
        }
        return matchSets;
      }, [] as Match3.BoardPosition[][]);
    },

    getLastTurnRewards: () => match3.turns[match3.turns.length - 2]?.rewards,

    getTotalRewards: () => {
      const totalRewards = {
        points: 0,
        gems: 0,
      };
      match3.turns?.forEach(({ rewards }) => {
        totalRewards.points += rewards.points;
        totalRewards.gems += rewards.gems;
      });
      return totalRewards;
    },
  });
};
